import React, { useEffect, useRef, useState } from "react";
import { getFirestore, collection, getDocs, query, where, doc, updateDoc, setDoc, getDoc, onSnapshot } from "firebase/firestore";
import app from "../../firebase";
import styles from "./uiStyles";
import html2pdf from "html2pdf.js";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { registrarAuth } from "../../firebase";

function EnrollModal({ student, termSchedules, termMiscSnapshot, termKey, schoolYearStart, schoolYearEnd, onClose }) {
  const [studentId, setStudentId] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const corRef = useRef();

  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }

  const studentName = `${student.lastName}, ${student.firstName}`;
  const studentIdNum = student.idNumber;
  const program = student.program || "N/A";
  const yearLevel = student.preRegistration?.entryLevel || "N/A";
  const corTermKey = termKey || "";
  const [termSchoolYear, termSemester] = corTermKey.split("_");
  const hasStoredCredentials = Boolean(student.studentId && student.studentpassword);
  const effectiveStudentId = studentId || student.studentId || "";
  const effectivePassword = password || student.studentpassword || "";
  const existingCor = corTermKey ? student.corHistory?.[corTermKey] : null;

  const groupedCourses = Array.isArray(termSchedules) && termSchedules.length > 0
    ? Object.values(
        termSchedules.reduce((acc, course) => {
          const key = [course.courseId, course.courseName, course.section].join('|');
          if (!acc[key]) {
            acc[key] = {
              courseId: course.courseId,
              courseName: course.courseName,
              section: course.section,
              units: course.units,
              meetings: []
            };
          }
          acc[key].meetings.push({
            day: course.day,
            time: course.time,
            room: course.room
          });
          return acc;
        }, {})
      )
    : [];

  const totalUnits = termMiscSnapshot?.totalUnits
    ? parseFloat(termMiscSnapshot.totalUnits)
    : Array.isArray(termSchedules)
      ? Object.values(
          termSchedules.reduce((acc, course) => {
            const key = [course.courseId, course.courseName, course.section].join('|');
            if (!acc[key]) acc[key] = { units: course.units };
            return acc;
          }, {})
        ).reduce((sum, c) => sum + (parseFloat(c.units) || 0), 0)
      : 0;

  let total = 0;
  const fees = (termMiscSnapshot?.fees || []).map((fee) => {
    let amount = 0;
    let formula = "";
    if (fee.formula?.toLowerCase().includes("units") && typeof fee.amount === "number") {
      amount = totalUnits * fee.amount;
      formula = `(${totalUnits} units X ${fee.amount})`;
    } else if (fee.perUnit !== undefined) {
      amount = totalUnits * fee.perUnit;
      formula = `(${totalUnits} units X ${fee.perUnit})`;
    } else if (fee.formula) {
      formula = fee.formula;
      amount = fee.amount || 0;
    } else {
      amount = fee.amount || 0;
    }
    total += amount;
    return { ...fee, amount, formula };
  });

  const cashDiscount = termMiscSnapshot?.cashDiscount || 0;
  const totalWithDiscount = total - cashDiscount;
  const installment = parseFloat((total / 5).toFixed(2));
  const cashWithDiscountDate = termMiscSnapshot?.cashWithDiscountDate || "";
  const cashWithoutDiscountDate = termMiscSnapshot?.cashWithoutDiscountDate || "";
  const installmentDates = termMiscSnapshot?.installmentDates || [];

  async function sendEnrollmentEmail(studentIdValue, passwordValue) {
    try {
      console.log("Sending enrollment email...");
      const idToSend = studentIdValue || effectiveStudentId;
      const passToSend = passwordValue || effectivePassword;
      if (!student.email || !idToSend || !passToSend) {
        console.error("Missing email fields", { hasEmail: !!student.email, hasId: !!idToSend, hasPass: !!passToSend });
        return false;
      }

      const emailData = {
        to: student.email,
        name: `${student.lastName}, ${student.firstName}`,
        idNumber: idToSend,
        password: passToSend,
        program: student.program || "N/A",
        template: "enrollmentApproval"
      };

      console.log("Email payload:", JSON.stringify(emailData, null, 2));

      const response = await fetch(
        "https://sendapprovalemail-ag2nilnpda-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(emailData)
        }
      );

      console.log("Email response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Email API error response:", errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get("content-type");
      let result;
      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.log("Email API response (text):", text);
        result = { message: text };
      }

      console.log("Email sent successfully:", result);
      return true;
    } catch (err) {
      console.error("Error sending enrollment email:", err);
      return false;
    }
  }

  async function handleGenerate() {
    try {
      setSaving(true);
      // Simple ID/password generation - keep deterministic if idNumber exists
      const generatedId = student.studentId || student.idNumber || `S${Date.now().toString().slice(-6)}`;
      const generatedPass = Math.random().toString(36).slice(-8) + Math.random().toString(36).toUpperCase().slice(-2);

      // Persist to Firestore
      const db = getFirestore(app);
      if (!student?.id) {
        throw new Error('Missing student document id');
      }
      const studentRef = doc(db, "students", student.id);
      await updateDoc(studentRef, {
        studentId: generatedId,
        studentpassword: generatedPass
      });

      setStudentId(generatedId);
      setPassword(generatedPass);
      setSaving(false);
      alert("Student ID and password generated and saved.");
    } catch (err) {
      console.error("Error generating credentials:", err);
      setSaving(false);
      alert("Failed to generate credentials. See console for details.");
    }
  }

  async function handlePrintAndSavePdf() {
    try {
      // Check if studentId and password are generated
      if (!effectiveStudentId || !effectivePassword) {
        alert("Please click 'Generate' button first to create Student ID and Password!");
        return;
      }
      setSaving(true);

      const courseTableRows = groupedCourses.map(row => `
        <tr>
          <td style="padding:8px; border-bottom:1px solid #bbb;">${row.courseId || ""}</td>
          <td style="padding:8px; border-bottom:1px solid #bbb;">${row.courseName || ""}</td>
          <td style="padding:8px; border-bottom:1px solid #bbb; text-align:center;">${row.section || ""}</td>
          <td style="padding:8px; border-bottom:1px solid #bbb;">
            ${row.meetings.map(m => `<div>${m.day}</div>`).join("")}
          </td>
          <td style="padding:8px; border-bottom:1px solid #bbb;">
            ${row.meetings.map(m => `<div>${m.time}</div>`).join("")}
          </td>
          <td style="padding:8px; border-bottom:1px solid #bbb;">
            ${row.meetings.map(m => `<div>${m.room}</div>`).join("")}
          </td>
          <td style="padding:8px; border-bottom:1px solid #bbb; text-align:right;">${row.units || ""}</td>
        </tr>
      `).join("");

      const feeTableRows = fees.map(fee => `
        <tr>
          <td style="padding:6px 0;">
            ${fee.label} ${fee.formula ? `<span style="color:#888;font-size:13px;">${fee.formula}</span>` : ''}
          </td>
          <td style="text-align:right; padding:6px 0;">
            ₱ ${fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </td>
        </tr>
      `).join("");

      const installmentTableRows = installmentDates.map((date, idx) => `
        <tr>
          <td style="padding:6px 0;">
            ${["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}
          </td>
          <td style="text-align:left; padding:6px 0;">
            ${formatDate(date)}
          </td>
          <td style="text-align:right; padding:6px 0;">
            ₱ ${installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </td>
        </tr>
      `).join("");

      const printWindow = window.open("", "_blank", "width=900,height=1200");
      if (!printWindow) {
        setSaving(false);
        return;
      }

      printWindow.document.write(`
        <html>
        <head>
          <title>Certificate of Registration</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
            .container { width: 900px; margin: 0 auto; padding: 32px; }
            .header { text-align: center; margin-bottom: 18px; }
            .subtitle { font-size: 20px; margin-bottom: 18px; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
            .subject-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
            .subject-table th, .subject-table td { padding: 6px 8px; font-size: 15px; border-bottom: 1px solid #bbb; }
            .subject-table th { background: #f7f7f7; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div style="font-size:22px; font-weight:bold;">Manila Adventist College</div>
              <div style="font-size:15px;">CERTIFICATE OF REGISTRATION</div>
              <div class="subtitle">${termSchoolYear ? `AY: ${termSchoolYear}` : (student.AY || "N/A")} ${termSemester || student.preRegistration?.semester || student.semester || "First Semester"}</div>
            </div>
            <div class="info-row">
              <div>Name: <b>${studentName}</b></div>
              <div>Student ID: <b>${studentIdNum}</b></div>
            </div>
            <div class="info-row">
              <div>Program: <b>${program}</b></div>
              <div>Year: <b>${yearLevel}</b></div>
            </div>
            <table class="subject-table">
              <thead>
                <tr style="background: #f7f7f7;">
                  <th style="text-align:center;">Subject Code</th>
                  <th style="text-align:center;">Subject Description</th>
                  <th style="text-align:center;">Section</th>
                  <th style="text-align:center;">Day</th>
                  <th style="text-align:center;">Schedule</th>
                  <th style="text-align:center;">Room</th>
                  <th style="text-align:center;">Units</th>
                </tr>
              </thead>
              <tbody>
                ${courseTableRows}
                <tr>
                  <td colspan="6" style="text-align:right; font-weight:bold; padding:8px; border-bottom:1px solid #bbb;">TOTAL UNITS</td>
                  <td style="text-align:right; font-weight:bold; padding:8px; border-bottom:1px solid #bbb;">${totalUnits}</td>
                </tr>
              </tbody>
            </table>

            <div style="margin-top:32px; display:flex; gap:48px;">
              <div style="flex:1;">
                <div style="font-weight:700; font-size:20px; margin-bottom:18px;">MISCELLANEOUS FEES</div>
                <table style="width:100%; border-collapse:collapse; margin-bottom:18px;">
                  <thead>
                    <tr style="border-bottom:2px solid #222;">
                      <th style="text-align:left; font-weight:500;"> </th>
                      <th style="text-align:right; font-weight:500;">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${feeTableRows}
                    <tr>
                      <td style="font-weight:700; padding-top:10px;">Total:</td>
                      <td style="text-align:right; font-weight:700; font-size:17px; padding-top:10px;">
                        ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div style="font-weight:700; color:#155c2b; font-size:18px; margin:30px 0 8px 0;">
                  OPTION 1: CASH PAYMENT WITH DISCOUNT
                </div>
                <div style="font-size:18px; margin-bottom:4px; font-weight:bold; color:#222;">
                  ₱ ${totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div style="font-size:16px; margin-bottom:18px;">
                  If full payment is made on or before ${formatDate(cashWithDiscountDate)}.
                </div>

                <div style="font-weight:700; color:#155c2b; font-size:18px; margin:30px 0 8px 0;">
                  OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
                </div>
                <div style="font-size:18px; margin-bottom:4px; font-weight:bold; color:#222;">
                  ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
                <div style="font-size:16px; margin-bottom:18px;">
                  If full payment is made after ${formatDate(cashWithoutDiscountDate)}.
                </div>
              </div>

              <div style="flex:1;">
                <div style="font-weight:700; color:#155c2b; font-size:20px; margin-bottom:18px;">
                  OPTION 3: INSTALLMENT PAYMENT
                </div>
                <table style="width:100%; border-collapse:collapse; margin-bottom:18px;">
                  <tbody>
                    ${installmentTableRows}
                    <tr>
                      <td style="font-weight:700; padding-top:10px;">Total:</td>
                      <td></td>
                      <td style="text-align:right; font-weight:700; font-size:17px; padding-top:10px;">
                        ₱ ${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style="margin-top:32px; font-size:13px; background:#fff8f1; border:1px solid #ff9800; border-radius:6px; padding:12px; color:#e67e22;">
              <div style="font-weight:bold;">IMPORTANT NOTICE:</div>
              <div style="color:#444;">
                1. Students with requirement deficiency will be placed as a PROBATIONARY STUDENT.<br>
                2. This registration form serves as the student's proof of enrollment. Students must attend only classes/sections as indicated on the registration form.<br>
                3. This computer-generated REGISTRATION FORM has been electronically approved by the Student, Program Head/SHS Principal, College Treasurer, and College Registrar. No signature required.<br>
              </div>
            </div>

            <div style="font-size:13px; margin-top:24px;">
              Printed on: ${formatDate(new Date().toISOString())}<br>
              Officially Enrolled on: ${formatDate(student.enrolledDate || new Date().toISOString())}
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();

      if (existingCor?.corPdfUrl) {
        window.open(existingCor.corPdfUrl, "_blank");
        setSaving(false);
        return;
      }

      const db = getFirestore(app);
      const studentRef = doc(db, "students", student.id);
      
      const printedAt = new Date().toISOString();
      const corHistoryPrintedUpdate = corTermKey
        ? {
            [`corHistory.${corTermKey}.corPdfGeneratedAt`]: printedAt,
            [`corHistory.${corTermKey}.printedAt`]: printedAt
          }
        : {};

      await updateDoc(studentRef, {
        ...corHistoryPrintedUpdate
      });
      console.log("COR print timestamp saved!");

      const element = corRef.current;
      if (!element) {
        alert("COR content not found!");
        setSaving(false);
        return;
      }

      const opt = {
        margin: 0,
        filename: `${student.lastName}_${student.firstName}_COR.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' }
      };

      console.log("Starting PDF generation...");
      const pdfBlob = await html2pdf().from(element).set(opt).outputPdf('blob');
      console.log("PDF blob created:", pdfBlob.size);

      const storage = getStorage(app);
      const pdfRef = storageRef(storage, `cor/${student.id}/${opt.filename}`);

      let metadata = { contentType: 'application/pdf' };
      try {
        const auth = registrarAuth;
        const user = auth?.currentUser;
        if (user?.uid) {
          metadata = {
            contentType: 'application/pdf',
            customMetadata: { ownerUid: user.uid }
          };
        }
      } catch (e) {
        console.warn('Could not read auth.currentUser for metadata', e);
      }

      console.log("Uploading PDF to Firebase Storage...");
      await uploadBytes(pdfRef, pdfBlob, metadata);
      console.log("PDF uploaded successfully!");

      let url = null;
      try {
        url = await getDownloadURL(pdfRef);
        console.log("PDF download URL:", url);
      } catch (err) {
        console.error('Error getting download URL for COR:', err);
      }

      const pdfGeneratedAt = new Date().toISOString();
      const corUpdateData = {};

      if (corTermKey) {
        corUpdateData[`corHistory.${corTermKey}.corPdfUrl`] = url || null;
        corUpdateData[`corHistory.${corTermKey}.pdfGeneratedAt`] = pdfGeneratedAt;
        corUpdateData[`corHistory.${corTermKey}.pdfFileName`] = opt.filename;
      }

      console.log("Updating COR with PDF URL...", corUpdateData);
      await updateDoc(studentRef, corUpdateData);
      console.log("COR PDF URL updated successfully!");

      // Send enrollment email
      await sendEnrollmentEmail(effectiveStudentId, effectivePassword);

      if (url) window.open(url, "_blank");

      alert("COR printed successfully!");

      setSaving(false);
    } catch (err) {
      console.error('handlePrintAndSavePdf error:', err);
      setSaving(false);
      alert('Failed to print/save COR: ' + err.message);
    }
  }

  return (
    <>
      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
        <div
          ref={corRef}
          style={{
            width: 800,
            margin: "0 auto",
            padding: 16,
            background: "#fff",
            fontSize: 11,
            lineHeight: 1.3,
            transform: "scale(0.9)",
            transformOrigin: "top center",
            boxSizing: "border-box",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 16, fontWeight: "bold" }}>Manila Adventist College</div>
            <div style={{ fontSize: 12 }}>CERTIFICATE OF REGISTRATION</div>
            <div style={{ fontSize: 14, marginBottom: 10 }}>
              {termSchoolYear ? `AY: ${termSchoolYear}` : (student.AY || "N/A")} {termSemester || student.preRegistration?.semester || student.semester || "First Semester"}
            </div>
          </div>

          <div style={{ marginBottom: 30 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <div>Name: <b>{studentName}</b></div>
              <div>Student ID: <b>{studentIdNum}</b></div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>Program: <b>{program}</b></div>
              <div>Year: <b>{yearLevel}</b></div>
            </div>
          </div>

          <div style={{ marginBottom: 35 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "center",
              }}
            >
              <thead>
                <tr>
                  {["Subject Code", "Subject Description", "Sec", "Day", "Time", "Room", "Units"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: 4,
                        borderBottom: "1px solid #bbb",
                        background: "#f7f7f7",
                        fontSize: 10,
                        textAlign: "center",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedCourses.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb" }}>{row.courseId || ""}</td>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb" }}>{row.courseName || ""}</td>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb" }}>{row.section || ""}</td>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb" }}>
                      {row.meetings.map((m, i) => (
                        <div key={i}>{m.day}</div>
                      ))}
                    </td>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb" }}>
                      {row.meetings.map((m, i) => (
                        <div key={i}>{m.time}</div>
                      ))}
                    </td>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb" }}>
                      {row.meetings.map((m, i) => (
                        <div key={i}>{m.room}</div>
                      ))}
                    </td>
                    <td style={{ padding: "4px", borderBottom: "1px solid #bbb", textAlign: "right" }}>
                      {row.units || ""}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={6} style={{ textAlign: "right", fontWeight: "bold", padding: "4px", borderBottom: "1px solid #bbb" }}>TOTAL UNITS</td>
                  <td style={{ textAlign: "right", fontWeight: "bold", padding: "4px", borderBottom: "1px solid #bbb" }}>
                    {totalUnits}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 40, marginBottom: 30, display: "flex", justifyContent: "center", gap: 40 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, textAlign: "center" }}>
                MISCELLANEOUS FEES
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                <tbody>
                  {fees.map((fee, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "3px 0", fontSize: 10 }}>
                        {fee.label}{" "}
                        <span style={{ color: "#888", fontSize: 9 }}>{fee.formula}</span>
                      </td>
                      <td style={{ textAlign: "right", padding: "3px 0", fontSize: 10 }}>
                        ₱ {fee.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700, paddingTop: 4 }}>Total:</td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 11, paddingTop: 4 }}>
                      ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ fontWeight: 700, color: "#155c2b", fontSize: 11, margin: "12px 0 4px" }}>
                OPTION 1: CASH PAYMENT WITH DISCOUNT
              </div>
              <div style={{ fontSize: 11, fontWeight: "bold", color: "#222" }}>
                ₱ {totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 9, marginBottom: 8 }}>
                If full payment is made on or before {formatDate(cashWithDiscountDate)}.
              </div>

              <div style={{ fontWeight: 700, color: "#155c2b", fontSize: 11, margin: "8px 0 4px" }}>
                OPTION 2: CASH PAYMENT WITHOUT DISCOUNT
              </div>
              <div style={{ fontSize: 11, fontWeight: "bold", color: "#222" }}>
                ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 9, marginBottom: 8 }}>
                If full payment is made after {formatDate(cashWithoutDiscountDate)}.
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#155c2b", fontSize: 12, marginBottom: 6, textAlign: "center" }}>
                OPTION 3: INSTALLMENT PAYMENT
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
                <tbody>
                  {installmentDates.map((date, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "6px 0" }}>
                        {["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}
                      </td>
                      <td style={{ textAlign: "left", padding: "6px 0" }}>
                        {formatDate(date)}
                      </td>
                      <td style={{ textAlign: "right", padding: "6px 0" }}>
                        ₱ {installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700, paddingTop: 10 }}>Total:</td>
                    <td></td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 11, paddingTop: 10 }}>
                      ₱ {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              marginTop: 20,
              fontSize: 9,
              background: "#fff8f1",
              border: "1px solid #ff9800",
              borderRadius: 4,
              padding: 8,
              color: "#e67e22",
              textAlign: "left",
            }}
          >
            <div style={{ fontWeight: "bold" }}>IMPORTANT NOTICE:</div>
            <div style={{ color: "#444" }}>
              1. Students with requirement deficiency will be placed as a PROBATIONARY STUDENT.<br />
              2. This registration form serves as the student's proof of enrollment.<br />
              3. This computer-generated REGISTRATION FORM has been electronically approved.<br />
            </div>
          </div>

          <div style={{ fontSize: 9, marginTop: 14, textAlign: "center" }}>
            Printed on: {formatDate(new Date().toISOString())}<br />
            Officially Enrolled on: {formatDate(student.enrolledDate || new Date().toISOString())}
          </div>
        </div>
      </div>

      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.35)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          background: '#fff', borderRadius: 10, padding: '0 0 32px 0',
          minWidth: 600, maxWidth: 600, width: '100%',
          boxShadow: '0 2px 16px rgba(0,0,0,0.13)', position: 'relative',
          overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto'
        }}>
          <div style={{ fontWeight: 700, fontSize: 28, textAlign: "center", margin: "40px 0 32px 0" }}>
            Student Enrollment
          </div>
          <div style={{ padding: "0 60px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", rowGap: 18, columnGap: 18, fontSize: 18 }}>
              <div style={{ fontWeight: 600 }}>Name:</div>
              <div>{student.lastName}, {student.firstName}</div>
              <div style={{ fontWeight: 600 }}>ID Number:</div>
              <div>{student.studentId || student.idNumber}</div>
              {!hasStoredCredentials && (
                <>
                  <div style={{ fontWeight: 600 }}>Student ID:</div>
                  <input
                    style={{ ...styles.input, background: '#eee', border: 'none', padding: '8px 12px', fontSize: 16 }}
                    value={effectiveStudentId}
                    disabled
                  />
                </>
              )}
              {!hasStoredCredentials && (
                <>
                  <div style={{ fontWeight: 600 }}>Password:</div>
                  <input
                    type="password"
                    style={{ ...styles.input, background: '#eee', border: 'none', padding: '8px 12px', fontSize: 16 }}
                    value={password}
                    disabled
                  />
                </>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 18, marginTop: 40 }}>
              {!hasStoredCredentials && (
                <button style={{ ...styles.btnNeutral, cursor: saving ? 'not-allowed' : 'pointer' }} onClick={handleGenerate} disabled={saving}>
                  {saving ? "Saving..." : "Generate"}
                </button>
              )}
              <button style={{ ...styles.btnPrimary, cursor: saving ? 'not-allowed' : 'pointer' }} onClick={handlePrintAndSavePdf} disabled={!effectiveStudentId || !effectivePassword || saving}>
                {saving ? "Saving..." : "Print & Save PDF"}
              </button>
              <button style={styles.modalBtnClose} onClick={onClose}>CLOSE</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function ReviewEnrollment() {
  const [students, setStudents] = useState([]);
  const [idFilter, setIdFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [actionMenu, setActionMenu] = useState(null);
  const [viewStudent, setViewStudent] = useState(null);
  const [enrollStudent, setEnrollStudent] = useState(null);

  const [miscFees, setMiscFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(0);
  const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
  const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
  const [installmentDates, setInstallmentDates] = useState([]);
  const [schoolYearStart, setSchoolYearStart] = useState("");
  const [schoolYearEnd, setSchoolYearEnd] = useState("");
  const [assessmentSchoolYear, setAssessmentSchoolYear] = useState("");
  const [assessmentSemester, setAssessmentSemester] = useState("");

  useEffect(() => {
    const db = getFirestore(app);
    const q = query(
      collection(db, "students"),
      where("registrationStatus", "in", ["submitted", "enrolled"])
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudents(data);

      if (viewStudent) {
        const updatedView = data.find(s => s.id === viewStudent.id);
        if (updatedView) {
          setViewStudent(updatedView);
        }
      }

      if (enrollStudent) {
        const updatedEnroll = data.find(s => s.id === enrollStudent.id);
        if (updatedEnroll) {
          setEnrollStudent(updatedEnroll);
        }
      }
    }, (err) => {
      console.error("Error fetching students:", err);
    });

    const feesRef = doc(db, "systemSettings", "miscellaneousFees");
    const feesUnsubscribe = onSnapshot(feesRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setMiscFees(data.fees || []);
        setCashDiscount(data.cashDiscount || 0);
        setCashWithDiscountDate(data.cashWithDiscountDate || "");
        setCashWithoutDiscountDate(data.cashWithoutDiscountDate || "");
        setInstallmentDates(data.installmentDates || []);
      }
    }, (err) => {
      console.error("Error fetching fees:", err);
    });

    const announcementRef = doc(db, "systemSettings", "announcement");
    const announcementUnsubscribe = onSnapshot(announcementRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSchoolYearStart(data.schoolYearStart || "");
        setSchoolYearEnd(data.schoolYearEnd || "");
      }
    }, (err) => {
      console.error("Error fetching announcement:", err);
    });

    return () => {
      unsubscribe();
      feesUnsubscribe();
      announcementUnsubscribe();
    };
  }, []);

  const filtered = students.filter((s) => {
    const studentId = (s.studentId || s.idNumber || "").toString();
    const studentName = `${s.lastName ?? ""}, ${s.firstName ?? ""}`;
    const idMatch = studentId.includes(idFilter);
    const nameMatch = studentName.toLowerCase().includes(nameFilter.toLowerCase());
    const categoryMatch = categoryFilter === "all"
      || (s.category || "Enrolled Students").toLowerCase() === categoryFilter.toLowerCase();
    return idMatch && nameMatch && categoryMatch;
  });
  const clearFilters = () => {
    setIdFilter("");
    setNameFilter("");
    setCategoryFilter("all");
    setPage(1);
  };
  const rowsPerPage = 10;
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  function formatDate(dateStr) {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  }

  const getTermKeys = (student) => {
    const scheduleKeys = Object.keys(student?.termSchedules || {});
    const miscKeys = Object.keys(student?.miscellaneousFeesHistory || {});
    return Array.from(new Set([...scheduleKeys, ...miscKeys])).filter(Boolean);
  };

  useEffect(() => {
    if (!viewStudent) {
      setAssessmentSchoolYear("");
      setAssessmentSemester("");
      return;
    }
    const termKeys = getTermKeys(viewStudent);
    if (termKeys.length === 0) {
      setAssessmentSchoolYear("");
      setAssessmentSemester("");
      return;
    }
    const [schoolYear, semester] = termKeys[termKeys.length - 1].split("_");
    setAssessmentSchoolYear(schoolYear || "");
    setAssessmentSemester(semester || "");
  }, [viewStudent]);

  const assessmentTermKey = assessmentSchoolYear && assessmentSemester
    ? `${assessmentSchoolYear}_${assessmentSemester}`
    : "";
  const assessmentSchedules = assessmentTermKey
    ? (viewStudent?.termSchedules?.[assessmentTermKey] || [])
    : [];
  const assessmentMiscEntries = assessmentTermKey
    ? (viewStudent?.miscellaneousFeesHistory?.[assessmentTermKey] || [])
    : [];
  const assessmentMiscSnapshot = Array.isArray(assessmentMiscEntries) && assessmentMiscEntries.length > 0
    ? assessmentMiscEntries[assessmentMiscEntries.length - 1]
    : null;

  // Compute term data for enrollStudent (when enrolling directly from table or from View modal)
  const enrollTermKeys = enrollStudent ? getTermKeys(enrollStudent) : [];
  const enrollLatestTermKey = enrollTermKeys.length > 0 ? enrollTermKeys[enrollTermKeys.length - 1] : "";
  const enrollSchedulesLatest = enrollLatestTermKey
    ? (enrollStudent?.termSchedules?.[enrollLatestTermKey] || [])
    : [];
  const enrollMiscEntriesLatest = enrollLatestTermKey
    ? (enrollStudent?.miscellaneousFeesHistory?.[enrollLatestTermKey] || [])
    : [];
  const enrollMiscSnapshotLatest = Array.isArray(enrollMiscEntriesLatest) && enrollMiscEntriesLatest.length > 0
    ? enrollMiscEntriesLatest[enrollMiscEntriesLatest.length - 1]
    : null;

  // Use selected term data from View modal if enrolling from there, otherwise use latest term
  const enrollFromView = enrollStudent && viewStudent && enrollStudent.id === viewStudent.id;
  const enrollTermKey = enrollFromView
    ? assessmentTermKey
    : enrollLatestTermKey;
  const enrollSchedules = enrollFromView
    ? assessmentSchedules
    : enrollSchedulesLatest;
  const enrollMiscSnapshot = enrollFromView
    ? assessmentMiscSnapshot
    : enrollMiscSnapshotLatest;

  async function handleEnroll(student) {
    try {
      const db = getFirestore(app);
      const studentRef = doc(db, "students", student.id);
      await updateDoc(studentRef, {
        registrationStatus: "enrolled",
        category: "Enrolled",
        enrolledDate: new Date().toISOString()
      });
      setEnrollStudent(student);
    } catch (err) {
      console.error("Error enrolling student:", err);
      alert("Failed to enroll student. Check console for details.");
    }
  }

  const checkRequirementsClearance = (student) => {
    if (!student) return { isCleared: false, missingReqs: [] };
    
    const requirements = student.requirements || {};
    const missingReqs = [];
    
    if (!requirements.pic) missingReqs.push("2x2 Picture");
    if (!requirements.cert) missingReqs.push("Birth/Marriage Certificate");
    if (!requirements.id) missingReqs.push("Latest School/Government ID");
    if (!requirements.grades) missingReqs.push("Copy of Grades");
    
    return { isCleared: missingReqs.length === 0, missingReqs };
  };

  const calculateBalance = (student) => {
    if (!student) return 0;
    
    const ledger = student.financialLedger || [];
    let totalBillings = 0;
    let totalPayments = 0;
    
    ledger.forEach((entry) => {
      if (entry.type === "billing") {
        totalBillings += entry.amount || 0;
      } else if (entry.type === "payment") {
        totalPayments += entry.amount || 0;
      }
    });
    
    return totalBillings - totalPayments;
  };

  return (
    <div style={styles.page}>
      <div style={styles.title}>Review Enrollment</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <input
          type="text"
          placeholder="Filter by ID Number"
          value={idFilter}
          inputMode="numeric"
          maxLength={10}
          onChange={e => { setIdFilter(e.target.value.replace(/\D/g, "").slice(0, 10)); setPage(1); }}
          style={{ ...styles.input, width: 240, background: '#fff', border: '1px solid #ddd' }}
        />
        <input
          type="text"
          placeholder="Filter by Name"
          value={nameFilter}
          maxLength={20}
          onChange={e => { setNameFilter(e.target.value.replace(/[^\p{L}\s]/gu, "").slice(0, 20)); setPage(1); }}
          style={{ ...styles.input, width: 280, background: '#fff', border: '1px solid #ddd' }}
        />
        <select
          value={categoryFilter}
          onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
          style={{ ...styles.input, width: 220, background: '#fff', border: '1px solid #ddd' }}
        >
          <option value="all">All Categories</option>
          <option value="New Student">New Student</option>
          <option value="Transferee">Transferee</option>
          <option value="Enrolled">Enrolled</option>
        </select>
        <button
          onClick={clearFilters}
          style={{ background: '#e67e22', color: '#fff', border: '1px solid #e67e22', borderRadius: 6, padding: '10px 20px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
        >
          Clear All
        </button>
      </div>
      <table style={styles.tableWrapper}>
        <thead>
          <tr style={styles.theadRow}>
            <th style={{ ...styles.th, textAlign: "center" }}>ID Number</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Name</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Category</th>
            <th style={{ ...styles.th, textAlign: "center" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((student, idx) => (
            <tr key={student.id || idx} style={styles.trBorder}>
              <td style={{ ...styles.td, textAlign: "center" }}>{student.studentId || student.idNumber || "EXAMPLE"}</td>
              <td style={{ ...styles.td, textAlign: "center" }}>{student.lastName && student.firstName ? `${student.lastName}, ${student.firstName}` : "EXAMPLE"}</td>
              <td style={{ ...styles.td, textAlign: "center" }}>{student.category || "Enrolled Students"}</td>
              <td style={{ ...styles.td, textAlign: "center" }}>
                <button style={styles.btnPrimary} onClick={() => setViewStudent(student)}>View</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 24, textAlign: "center" }}>
        <button
          onClick={() => setPage(page > 1 ? page - 1 : 1)}
          disabled={page === 1}
          style={{ marginRight: 16, background: 'none', border: 'none', fontSize: 24, color: styles.iconButton.color || '#155c2b' }}
        >
          &#8592;
        </button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            style={{
              margin: "0 4px",
              background: page === i + 1 ? styles.paginationActive.background : 'none',
              color: page === i + 1 ? styles.paginationActive.color : (styles.iconButton.color || '#155c2b'),
              border: "none",
              borderRadius: 4,
              fontWeight: page === i + 1 ? 700 : 400,
              fontSize: 18,
              padding: "2px 10px"
            }}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
          disabled={page === totalPages}
          style={{ marginLeft: 16, background: 'none', border: 'none', fontSize: 24, color: styles.iconButton.color || '#155c2b' }}
        >
          &#8594;
        </button>
      </div>

      {viewStudent && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modalBoxLarge, minWidth: 900, maxWidth: 1100, overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '2px solid #222',
              padding: '32px 48px 16px 48px',
              background: '#fff'
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Department:</div>
                <div style={{ fontSize: 16, marginBottom: 8 }}>
                  {viewStudent.program || "N/A"}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>School Year:</div>
                <select
                  value={assessmentSchoolYear}
                  onChange={e => {
                    setAssessmentSchoolYear(e.target.value);
                    setAssessmentSemester("");
                  }}
                  style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', minWidth: 160 }}
                >
                  <option value="">Select</option>
                  {Array.from(new Set(getTermKeys(viewStudent).map(k => k.split("_")[0]).filter(Boolean)))
                    .map(sy => (
                      <option key={sy} value={sy}>{sy}</option>
                    ))}
                </select>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Semester:</div>
                <select
                  value={assessmentSemester}
                  onChange={e => setAssessmentSemester(e.target.value)}
                  style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', minWidth: 160 }}
                  disabled={!assessmentSchoolYear}
                >
                  <option value="">Select</option>
                  {Array.from(new Set(
                    getTermKeys(viewStudent)
                      .filter(k => k.startsWith(`${assessmentSchoolYear}_`))
                      .map(k => k.split("_")[1])
                      .filter(Boolean)
                  )).map(sem => (
                    <option key={sem} value={sem}>{sem}</option>
                  ))}
                </select>
              </div>
              <button style={styles.modalBtnClose} onClick={() => setViewStudent(null)}>Close</button>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 32,
              padding: '32px 48px 0 48px',
              background: '#fff'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 10, textAlign: 'left' }}>Basic Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', fontSize: 16 }}>
                  <div>
                    <div style={{ marginBottom: 16 }}><b>Last Name:</b> {viewStudent.lastName || "N/A"}</div>
                    <div style={{ marginBottom: 16 }}><b>First Name:</b> {viewStudent.firstName || "N/A"}</div>
                    <div style={{ marginBottom: 16 }}><b>Middle Name:</b> {viewStudent.middleName || "N/A"}</div>
                    <div><b>Gender:</b> {viewStudent.gender || "N/A"}</div>
                  </div>
                  <div>
                    <div style={{ marginBottom: 16 }}><b>ID:</b> {viewStudent.studentId || viewStudent.idNumber || "N/A"}</div>
                    <div style={{ marginBottom: 16 }}><b>Mobile No.:</b> {viewStudent.mobile || "N/A"}</div>
                    <div><b>Email Address:</b> {viewStudent.email || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ height: 1, background: '#ddd', margin: '24px 48px 0 48px' }} />

            {/* Requirements Checklist */}
            <div style={{ padding: '24px 48px 0 48px' }}>
              {(() => {
                const requirementsClearance = checkRequirementsClearance(viewStudent);
                const hasMissingRequirements = !requirementsClearance.isCleared;

                return (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 16 }}>Requirements Checklist</div>
                    {hasMissingRequirements ? (
                      <div style={{ background: '#fff3cd', border: '2px solid #ffc107', borderRadius: 8, padding: 16, color: '#856404' }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>⚠ Missing Requirements</div>
                        <div style={{ marginBottom: 8, fontSize: 14 }}>
                          Please submit the following requirements to the Registrar Office:
                        </div>
                        <ul style={{ marginTop: 8, marginLeft: 20, lineHeight: 1.6, fontSize: 14 }}>
                          {requirementsClearance.missingReqs.map((req) => (
                            <li key={req} style={{ color: '#d9534f' }}>
                              ✗ {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div style={{ background: '#d4edda', border: '2px solid #28a745', borderRadius: 8, padding: 16, color: '#155601' }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>✓ All Requirements Submitted</div>
                        <div style={{ fontSize: 14 }}>You have successfully submitted all required documents.</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ height: 1, background: '#ddd', margin: '24px 48px 0 48px' }} />

            {/* Course Line-up */}
            <div style={{ padding: '32px 48px 0 48px', background: '#fff' }}>
              <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18, letterSpacing: 1 }}>Course Line-up</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15, marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: "#f7f7f7" }}>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Subject Code</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "left" }}>Subject Description</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Section</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Day</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Schedule</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Room</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Instructor</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "right" }}>Units</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(assessmentSchedules) && assessmentSchedules.length > 0 ? (
                    Object.values(
                      assessmentSchedules.reduce((acc, course) => {
                        const key = [course.courseId, course.courseName, course.section].join('|');
                        if (!acc[key]) acc[key] = {
                          courseId: course.courseId,
                          courseName: course.courseName,
                          section: course.section,
                          units: course.units,
                          meetings: []
                        };
                        acc[key].meetings.push({
                          day: course.day,
                          time: course.time,
                          room: course.room,
                          prof: course.prof
                        });
                        return acc;
                      }, {})
                    ).map((row, idx) => (
                      <tr key={row.courseId + '-' + row.section} style={{ borderBottom: "1px solid #ddd" }}>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>{row.courseId}</td>
                        <td style={{ padding: "8px", verticalAlign: "top" }}>{row.courseName}</td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>{row.section}</td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.day}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.time}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.room}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.prof}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "right" }}>{row.units}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ color: "#888", fontSize: 16, textAlign: "center", padding: "24px 0" }}>No course lineup available.</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={7} style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>TOTAL UNITS</td>
                    <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>
                      {Array.isArray(assessmentSchedules)
                        ? Object.values(
                            assessmentSchedules.reduce((acc, course) => {
                              const key = [course.courseId, course.courseName, course.section].join('|');
                              if (!acc[key]) acc[key] = { units: parseFloat(course.units) || 0 };
                              return acc;
                            }, {})
                          ).reduce((sum, row) => sum + (parseFloat(row.units) || 0), 0).toFixed(1)
                        : "0"
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Miscellaneous Fees */}
            <div style={{ padding: '0 48px 0 48px', background: '#fff' }}>
              {(() => {
                if (!assessmentMiscSnapshot) {
                  return (
                    <div style={{ color: '#888', fontSize: 16, textAlign: 'center', padding: '24px 0' }}>
                      No miscellaneous fees assessment available for the selected term.
                    </div>
                  );
                }

                const units = typeof assessmentMiscSnapshot.totalUnits === 'number'
                  ? assessmentMiscSnapshot.totalUnits
                  : 0;
                let total = 0;
                const feeRows = (assessmentMiscSnapshot.fees || []).map((fee, idx) => {
                  let amount = 0;
                  let formula = "";
                  if (
                    fee.formula &&
                    fee.formula.toLowerCase().includes("units") &&
                    typeof fee.amount === "number"
                  ) {
                    amount = units * fee.amount;
                    formula = `(${units} units X ${fee.amount})`;
                  } else if (fee.perUnit !== undefined) {
                    amount = units * fee.perUnit;
                    formula = `(${units} units X ${fee.perUnit})`;
                  } else if (fee.formula) {
                    formula = fee.formula;
                    amount = fee.amount || 0;
                  } else {
                    amount = fee.amount || 0;
                  }
                  total += amount;
                  return (
                    <tr key={idx}>
                      <td>
                        {fee.label}
                        {formula && (
                          <span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
                            {formula}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', borderBottom: '2px solid #bdbdbd', color: '#4b3c0a' }}>
                        {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                });

                const cashDiscountSnapshot = assessmentMiscSnapshot.cashDiscount || 0;
                const totalWithDiscount = total - cashDiscountSnapshot;
                const installment = parseFloat((total / 5).toFixed(2));

                return (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18, letterSpacing: 1 }}>MISCELLANEOUS FEES</div>
                    <table style={{ width: '100%', fontSize: 16, marginBottom: 18 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #222' }}>
                          <th style={{ textAlign: 'left', fontWeight: 500 }}> </th>
                          <th style={{ textAlign: 'right', fontWeight: 500 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeRows}
                        <tr>
                          <td style={{ fontWeight: 700, paddingTop: 10 }}>Total:</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
                            {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div style={{ fontWeight: 700, color: '#155c2b', fontSize: 17, margin: '30px 0 8px 0' }}>
                      OPTION 1: CASH PAYMENT WITH DISCOUNT <span style={{ float: 'right' }}>{totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#222', fontSize: 15, marginBottom: 18 }}>
                      If full payment is made on or before {assessmentMiscSnapshot.cashWithDiscountDate ? formatDate(assessmentMiscSnapshot.cashWithDiscountDate) : "N/A"}.
                    </div>
                    <div style={{ fontWeight: 700, color: '#155c2b', fontSize: 17, margin: '30px 0 8px 0' }}>
                      OPTION 2: CASH PAYMENT WITHOUT DISCOUNT <span style={{ float: 'right' }}>{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#222', fontSize: 15, marginBottom: 18 }}>
                      If full payment is made after {assessmentMiscSnapshot.cashWithoutDiscountDate ? formatDate(assessmentMiscSnapshot.cashWithoutDiscountDate) : "N/A"}.
                    </div>
                    <div style={{ fontWeight: 700, color: '#155c2b', fontSize: 17, margin: '30px 0 8px 0' }}>
                      OPTION 3: INSTALLMENT PAYMENT
                    </div>
                    <table style={{ width: '100%', fontSize: 16, marginBottom: 18 }}>
                      <tbody>
                        {(assessmentMiscSnapshot.installmentDates || []).map((date, idx) => (
                          <tr key={idx}>
                            <td>{["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}</td>
                            <td>{date ? formatDate(date) : "N/A"}</td>
                            <td style={{ textAlign: 'right' }}>{installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ fontWeight: 700 }}>Total</td>
                          <td></td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                );
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '32px 48px 0 48px' }}>
              <button
                style={{
                  background: '#e67e22',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '12px 38px',
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: 1,
                  boxShadow: '0 2px 8px rgba(39,174,96,0.07)',
                  cursor: 'pointer'
                }}
                onClick={() => handleEnroll(viewStudent)}
              >
                Enroll
              </button>
            </div>
          </div>
        </div>
      )}

      {enrollStudent && (
        <EnrollModal
          student={enrollStudent}
          termSchedules={enrollSchedules}
          termMiscSnapshot={enrollMiscSnapshot}
          termKey={enrollTermKey}
          schoolYearStart={schoolYearStart}
          schoolYearEnd={schoolYearEnd}
          onClose={() => setEnrollStudent(null)}
        />
      )}
    </div>
  );
}